import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface CreateGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (groupData: { name: string; note: string }) => void;
  selectedPatientsCount: number;
  isLoading?: boolean;
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  open,
  onOpenChange,
  onSave,
  selectedPatientsCount,
  isLoading = false
}) => {
  const [groupName, setGroupName] = useState("");
  const [note, setNote] = useState("");

  const handleSave = () => {
    if (groupName.trim()) {
      onSave({
        name: groupName.trim(),
        note: note.trim()
      });
      // Reset form
      setGroupName("");
      setNote("");
    }
  };

  const handleCancel = () => {
    // Reset form
    setGroupName("");
    setNote("");
    onOpenChange(false);
  };

  const isFormValid = groupName.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            Name Patient Group
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <p className="text-sm text-muted-foreground text-center">
            Please enter a name for this group before saving. This name will help you easily identify the group later.
          </p>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Input
                placeholder="Enter Group Name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="note" className="text-sm font-medium">
                Note
              </Label>
              <Textarea
                id="note"
                placeholder="Enter additional notes..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                className="w-full resize-none"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button 
            variant="secondary" 
            onClick={handleCancel}
            className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/90"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!isFormValid || isLoading}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};